import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, Users, Calendar, Zap, Shield, Star, Check, Scissors, Bell, PhoneOff, MessageSquareOff, UserX, ChevronRight } from "lucide-react";

const PhoneMockup = () => (
  <div className="relative mx-auto w-fit">
    <div className="relative w-[220px] sm:w-[270px] rounded-[32px] sm:rounded-[40px] border-[5px] sm:border-[6px] border-[#1a1a1a] bg-[#1a1a1a] shadow-2xl shadow-black/20 overflow-hidden transform rotate-1 hover:rotate-0 transition-transform duration-500">
      <div className="relative bg-[#1a1a1a] flex justify-center pt-1.5 pb-0.5 sm:pt-2 sm:pb-1">
        <div className="w-16 sm:w-20 h-[18px] sm:h-[22px] bg-[#1a1a1a] rounded-b-2xl" />
      </div>
      <div className="bg-white rounded-b-[27px] sm:rounded-b-[34px] overflow-hidden">
        <div className="bg-[#09090B] text-white px-4 sm:px-5 py-3 sm:py-4">
          <p className="font-heading font-bold text-[12px] sm:text-[13px]">Le Barber Shop</p>
          <div className="flex items-center gap-1 mt-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={9} className="text-yellow-400 fill-yellow-400" />
            ))}
            <span className="text-[8px] sm:text-[9px] text-white/50 ml-1">4.8 (120 avis)</span>
          </div>
        </div>
        <div className="px-3 sm:px-4 py-2.5 sm:py-3 space-y-1.5 sm:space-y-2">
          <div className="border-2 border-blue-500 bg-blue-50 rounded-xl p-2.5 sm:p-3 flex justify-between items-center">
            <div><p className="text-[10px] sm:text-[11px] font-bold text-blue-900">Coupe homme</p><p className="text-[8px] sm:text-[9px] text-blue-600/60">30 min</p></div>
            <p className="font-mono text-[12px] sm:text-[13px] font-bold text-blue-600">25&euro;</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-2.5 sm:p-3 flex justify-between items-center">
            <div><p className="text-[10px] sm:text-[11px] font-semibold">Coupe + Barbe</p><p className="text-[8px] sm:text-[9px] text-gray-400">45 min</p></div>
            <p className="font-mono text-[12px] sm:text-[13px] font-bold">35&euro;</p>
          </div>
          <div className="border border-gray-200 rounded-xl p-2.5 sm:p-3 flex justify-between items-center">
            <div><p className="text-[10px] sm:text-[11px] font-semibold">Barbe</p><p className="text-[8px] sm:text-[9px] text-gray-400">20 min</p></div>
            <p className="font-mono text-[12px] sm:text-[13px] font-bold">15&euro;</p>
          </div>
        </div>
        <div className="px-3 sm:px-4 pb-4 sm:pb-5">
          <div className="bg-[#2563EB] text-white text-center py-2.5 sm:py-3 rounded-full text-[10px] sm:text-[11px] font-bold tracking-wide">
            R&eacute;server maintenant
          </div>
        </div>
      </div>
    </div>
    <div className="absolute -left-16 top-16 bg-white rounded-xl p-3 shadow-lg border border-gray-100 opacity-0 animate-fade-up stagger-3 hidden lg:block">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center"><Check size={14} className="text-green-600" /></div>
        <div><p className="text-[11px] font-bold">R&eacute;servation confirm&eacute;e</p><p className="text-[9px] text-gray-400">il y a 30 sec</p></div>
      </div>
    </div>
    <div className="absolute -right-12 bottom-24 bg-white rounded-xl p-3 shadow-lg border border-gray-100 opacity-0 animate-fade-up stagger-5 hidden lg:block">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center"><Bell size={14} className="text-blue-600" /></div>
        <div><p className="text-[11px] font-bold">+3 r&eacute;servations</p><p className="text-[9px] text-gray-400">aujourd&apos;hui</p></div>
      </div>
    </div>
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const goRegister = () => navigate("/connexion?tab=register");
  const goDemo = () => window.open("/book/le-barber-shop", "_blank");

  return (
    <div className="min-h-screen bg-marketing-bg overflow-x-hidden" data-testid="landing-page">
      {/* Nav */}
      <nav className="glass-nav fixed top-0 inset-x-0 z-50 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scissors size={20} className="text-[#2563EB]" />
            <span className="font-heading font-bold text-lg sm:text-xl tracking-tight">CutIt</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#probleme" className="hover:text-foreground transition-colors">Pourquoi CutIt</a>
            <a href="#fonctionnement" className="hover:text-foreground transition-colors">Comment &ccedil;a marche</a>
            <a href="#tarifs" className="hover:text-foreground transition-colors">Tarifs</a>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" onClick={() => navigate("/connexion")} data-testid="nav-login-btn" className="text-xs sm:text-sm px-2 sm:px-4">Connexion</Button>
            <Button onClick={goRegister} data-testid="nav-cta-btn" className="rounded-full px-4 sm:px-6 text-xs sm:text-sm bg-[#2563EB] hover:bg-blue-700">Commencer</Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 pb-12 lg:pt-36 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-8 items-center overflow-hidden">
          <div className="opacity-0 animate-fade-up">
            <h1 className="text-2xl sm:text-4xl lg:text-[3.5rem] font-extrabold tracking-tight leading-[1.12] text-[#09090B] font-heading">
              Remplis ton agenda automatiquement.{" "}
              <span className="text-[#2563EB]">Sans appels. Sans Instagram.</span>
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-[#71717A] mt-5 sm:mt-6 max-w-lg leading-relaxed">
              Tes clients r&eacute;servent en 10 secondes pendant que tu travailles. CutIt, c&apos;est la page de r&eacute;servation ultra-simple pour barbiers.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 sm:mt-8">
              <Button onClick={goRegister} className="rounded-full px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-semibold bg-[#2563EB] hover:bg-blue-700 transition-all hover:scale-[1.03] active:scale-95 shadow-lg shadow-blue-500/20" data-testid="hero-cta-btn">
                Cr&eacute;er ma page gratuitement <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button variant="outline" onClick={goDemo} className="rounded-full px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-medium border-[#E4E4E7] hover:bg-white" data-testid="hero-demo-btn">
                Voir une d&eacute;mo
              </Button>
            </div>
            {/* Trust badges */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-x-6 sm:gap-y-2 mt-6 sm:mt-8 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Check size={14} className="text-green-500 shrink-0" /> Gratuit pour commencer</span>
              <span className="flex items-center gap-1.5"><Check size={14} className="text-green-500 shrink-0" /> 0% commission</span>
              <span className="flex items-center gap-1.5"><Check size={14} className="text-green-500 shrink-0" /> Pr&ecirc;t en 2 minutes</span>
            </div>
          </div>
          <div className="opacity-0 animate-fade-up stagger-2 flex justify-center lg:justify-end">
            <PhoneMockup />
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="py-8 sm:py-10 border-y border-black/5 bg-white/50">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 text-center">
            {[
              { value: "500+", label: "Barbiers actifs" },
              { value: "10 000+", label: "R\u00e9servations" },
              { value: "4.9/5", label: "Satisfaction" },
              { value: "<10s", label: "Pour r\u00e9server" },
            ].map((s, i) => (
              <div key={i}><p className="font-mono text-xl sm:text-2xl md:text-3xl font-bold text-[#09090B]">{s.value}</p><p className="text-[10px] sm:text-xs text-muted-foreground mt-1">{s.label}</p></div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="probleme" className="py-16 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-red-500 mb-3 block">Le probl&egrave;me</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#09090B]">Tu perds des clients chaque jour</h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-3">Pendant que tu coupes, tes clients essaient de te joindre. Et ils finissent chez un autre.</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: PhoneOff, color: "bg-red-100 text-red-600", title: "Appels manqu\u00e9s", desc: "Tu rates des r\u00e9servations pendant que tu travailles. Impossible de d\u00e9crocher les ciseaux \u00e0 la main." },
              { icon: MessageSquareOff, color: "bg-orange-100 text-orange-600", title: "DMs oubli\u00e9s", desc: "Tes messages Instagram d\u00e9bordent. Tu oublies de r\u00e9pondre et le client part ailleurs." },
              { icon: UserX, color: "bg-red-100 text-red-600", title: "No-shows", desc: "Des clients qui r\u00e9servent et ne viennent pas. Sans rappel, pas de moyen de les retenir." },
            ].map((p, i) => (
              <div key={i} className="bg-white p-5 sm:p-7 rounded-2xl border border-red-100/50 shadow-soft">
                <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl ${p.color} flex items-center justify-center mb-4 sm:mb-5`}>
                  <p.icon size={18} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold tracking-tight mb-2">{p.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="max-w-2xl mb-10 sm:mb-14">
            <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-[#2563EB] mb-3 block">La solution</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#09090B]">CutIt r&egrave;gle tout &ccedil;a</h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-3">Un lien. Tes clients r&eacute;servent seuls. Tu te concentres sur ton m&eacute;tier.</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: Zap, color: "bg-blue-100 text-blue-600", title: "R\u00e9servation automatique", desc: "Tes clients r\u00e9servent seuls, 24h/24, depuis un simple lien. Z\u00e9ro friction." },
              { icon: Bell, color: "bg-green-100 text-green-600", title: "Rappels automatiques", desc: "Notification avant chaque rendez-vous. Fini les no-shows et les trous dans l\u2019agenda." },
              { icon: Calendar, color: "bg-purple-100 text-purple-600", title: "Agenda simplifi\u00e9", desc: "Visualise ta journ\u00e9e, tes clients, tes revenus. Tout au m\u00eame endroit." },
            ].map((s, i) => (
              <div key={i} className="bg-[#FAFAFA] p-5 sm:p-7 rounded-2xl border border-border/50 hover:shadow-medium transition-shadow duration-300">
                <div className={`w-10 h-10 sm:w-11 sm:h-11 rounded-xl ${s.color} flex items-center justify-center mb-4 sm:mb-5`}>
                  <s.icon size={18} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold tracking-tight mb-2">{s.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="fonctionnement" className="py-16 lg:py-28 bg-marketing-bg">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#09090B]">Pr&ecirc;t en 2 minutes</h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-3">Pas de formation. Pas d&apos;installation. 3 &eacute;tapes.</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {[
              { step: "01", title: "Cr\u00e9e ta page", desc: "Renseigne tes services et horaires. Des pr\u00e9-r\u00e9glages sont l\u00e0 pour aller vite.", icon: Scissors },
              { step: "02", title: "Partage ton lien", desc: "Envoie-le par WhatsApp, Instagram, ou affiche le QR code dans ton salon.", icon: ChevronRight },
              { step: "03", title: "Tes clients r\u00e9servent", desc: "Ils r\u00e9servent en 10 secondes. Tu re\u00e7ois une notif \u00e0 chaque r\u00e9servation.", icon: Calendar },
            ].map((item, i) => (
              <div key={i} className="relative bg-white p-6 sm:p-8 rounded-2xl border border-border hover:border-blue-200 transition-colors duration-300 text-center">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#2563EB] text-white flex items-center justify-center font-mono font-bold text-base sm:text-lg mx-auto mb-4 sm:mb-5">{item.step}</div>
                <h3 className="text-base sm:text-lg font-semibold tracking-tight mb-2">{item.title}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#09090B] mb-8 sm:mb-12">Ils ont choisi CutIt</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { name: "Karim B.", role: "Le Barber Shop, Paris", text: "Mes r\u00e9servations ont augment\u00e9 de 40% le premier mois. Je ne r\u00e9ponds plus au t\u00e9l\u00e9phone entre deux clients." },
              { name: "Thomas R.", role: "Gentleman Coiffure, Lyon", text: "L\u2019onboarding est dingue. En 2 minutes j\u2019\u00e9tais pr\u00eat. Mes clients adorent la simplicit\u00e9." },
              { name: "Youssef M.", role: "Fresh Cut, Marseille", text: "Avant CutIt, je perdais 5 clients par semaine \u00e0 cause des appels manqu\u00e9s. Maintenant mon agenda est plein." },
            ].map((t, i) => (
              <div key={i} className="bg-[#FAFAFA] p-5 sm:p-7 rounded-2xl border border-border/50">
                <div className="flex gap-0.5 mb-3 sm:mb-4">{[...Array(5)].map((_, j) => <Star key={j} size={13} className="text-yellow-400 fill-yellow-400" />)}</div>
                <p className="text-xs sm:text-sm text-foreground leading-relaxed mb-4 sm:mb-5">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#2563EB]/10 flex items-center justify-center font-heading font-bold text-[#2563EB] text-sm">{t.name[0]}</div>
                  <div><p className="text-sm font-semibold">{t.name}</p><p className="text-xs text-muted-foreground">{t.role}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="tarifs" className="py-16 lg:py-28 bg-marketing-bg">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-[#09090B]">Tarifs simples. 0% commission.</h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-3">Commence gratuitement. Passe &agrave; Pro quand tu veux. Sans engagement.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
            <div className="bg-white p-6 sm:p-8 rounded-2xl border border-border">
              <h3 className="font-heading text-lg sm:text-xl font-bold mb-1">Gratuit</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-5 sm:mb-6">Pour d&eacute;marrer</p>
              <p className="font-mono text-4xl sm:text-5xl font-bold mb-1">0&euro;<span className="text-sm sm:text-base text-muted-foreground font-normal">/mois</span></p>
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-6 sm:mb-8">Aucune carte requise</p>
              <ul className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
                {["20 r\u00e9servations/mois", "Page de r\u00e9servation", "Tableau de bord", "Fichier client", "0% commission"].map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 sm:gap-3 text-xs sm:text-sm"><Check size={15} className="text-green-500 shrink-0" />{f}</li>
                ))}
              </ul>
              <Button variant="outline" className="w-full rounded-full py-5 sm:py-6 text-sm" onClick={goRegister} data-testid="pricing-free-btn">Commencer gratuitement</Button>
            </div>
            <div className="bg-white p-6 sm:p-8 rounded-2xl border-2 border-[#2563EB] relative">
              <div className="absolute -top-3 right-6 bg-[#2563EB] text-white text-[9px] sm:text-[10px] font-bold px-3 sm:px-4 py-1 sm:py-1.5 rounded-full tracking-wide">POPULAIRE</div>
              <h3 className="font-heading text-lg sm:text-xl font-bold mb-1">Pro</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-5 sm:mb-6">Pour les pros</p>
              <p className="font-mono text-4xl sm:text-5xl font-bold mb-1">29&euro;<span className="text-sm sm:text-base text-muted-foreground font-normal">/mois</span></p>
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-6 sm:mb-8">Sans engagement &middot; Annulable &agrave; tout moment</p>
              <ul className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
                {["R\u00e9servations illimit\u00e9es", "Tout du plan Gratuit", "Rappels SMS automatiques", "Support prioritaire", "0% commission"].map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 sm:gap-3 text-xs sm:text-sm"><Check size={15} className="text-[#2563EB] shrink-0" />{f}</li>
                ))}
              </ul>
              <Button className="w-full rounded-full py-5 sm:py-6 text-sm bg-[#2563EB] hover:bg-blue-700" onClick={goRegister} data-testid="pricing-pro-btn">Passer &agrave; Pro</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-28 bg-[#09090B]">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">Pr&ecirc;t &agrave; remplir ton agenda ?</h2>
          <p className="text-sm sm:text-base text-white/50 mb-6 sm:mb-8 max-w-lg mx-auto">Rejoins les 500+ barbiers qui ont choisi CutIt. Gratuit, sans engagement, pr&ecirc;t en 2 minutes.</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button onClick={goRegister} className="rounded-full px-8 sm:px-10 py-5 sm:py-7 text-sm sm:text-base font-semibold bg-white text-[#09090B] hover:bg-white/90 transition-all hover:scale-[1.03] active:scale-95" data-testid="final-cta-btn">
              Tester gratuitement <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button variant="ghost" onClick={goDemo} className="rounded-full px-8 sm:px-10 py-5 sm:py-7 text-sm sm:text-base text-white/70 hover:text-white hover:bg-white/10">
              Voir une d&eacute;mo
            </Button>
          </div>
          <p className="text-[10px] sm:text-xs text-white/30 mt-5 sm:mt-6">Cr&eacute;ation en 2 minutes &middot; Sans carte bancaire &middot; Sans engagement</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 bg-[#09090B] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-2"><Scissors size={16} className="text-white/40" /><span className="font-heading font-bold text-white/40 text-sm">CutIt</span></div>
          <p className="text-[10px] sm:text-xs text-white/30">&copy; 2026 CutIt. Tous droits r&eacute;serv&eacute;s.</p>
        </div>
      </footer>
    </div>
  );
}
